import React, {useState, useContext, useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./Applications.css";
import {
  Button,
  IconButton,
  ModalContext,
  TextInput,
} from "@spg/component_library";
import {SnackbarContext} from "../../reusables/SnackbarProvider";
import API, {Resolution} from "../../logic/API";
import {Application} from "../../logic/APITypes";
import {UploadFile} from "../../reusables/FileUpload";
import {Table} from "../../reusables/Table";
import {DefaultInputBehavior} from "../../reusables/DefaultInputBehavior";
import trashIcon from "../../images/trash.svg";
import {ScreenSize} from "../utility/ScreenSize";

/**
 * A page allowing admins to view and delete old or add new applications, that can be used in app domains.
 */
function Applications() {
  const intl = useIntl();
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const screenSize = useContext(ScreenSize);
  const modalContext = useContext(ModalContext);

  const [newApp, setNewApp] = useState("");
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [waitingForDeletion, setWaitingForDeletion] = useState<string[]>([]);
  const [apps, setApps] = useState<Resolution<Application[]>>({
    success: false,
    unresolved: true,
  });

  const repeatedNewAppName =
    apps.payload &&
    apps.payload.some(
      (app) => app.name.toLowerCase() == newApp.trim().toLowerCase(),
    );
  const validNewAppName = newApp.trim().length != 0 && !repeatedNewAppName;

  const deleteApp = (id: string) => {
    setWaitingForDeletion((old) => [...old, id]);
    API.deleteApplication(id).then((resolution) => {
      if (resolution.httpCode)
        if (resolution.httpCode === 204) {
          addSnackbar({
            variant: "timeout",
            text: intl.formatMessage({
              id: "applications.snackbar.delete.success",
            }),
            timeout: 7000,
          });
          setApps((oldApps) => ({
            ...oldApps,
            payload: (oldApps.payload || []).filter((app) => app.id !== id),
          }));
        } else if (resolution.httpCode >= 400) {
          addSnackbar({
            variant: "close",
            text: intl.formatMessage({
              id: "applications.snackbar.delete.error",
            }),
            hasCloseButton: true,
          });
        }
      setWaitingForDeletion((old) => old.filter((appId) => appId !== id));
    });
  };

  useEffect(() => {
    const getApps = async () => {
      setApps(await API.getApps());
    };
    getApps();
  }, []);

  return (
    <div className="landingPageGrid applicationsContainer">
      <h1 id="applicationsHeader">
        <FormattedMessage
          id="applications.title"
          values={{b: (chunks) => <b>{chunks}</b>}}
        />
      </h1>
      <div className="applicationsNewAppContainer">
        <DefaultInputBehavior
          component={TextInput}
          label={intl.formatMessage({id: "applications.newApp.label"})}
          idBase={"applicationsNewApp"}
          value={newApp}
          setValue={setNewApp}
          isError={repeatedNewAppName}
          isDisabled={waitingForResponse}
          supportingText={
            repeatedNewAppName
              ? intl.formatMessage({id: "applications.newApp.repeatedName"})
              : undefined
          }
        />
        <Button
          label={intl.formatMessage({id: "applications.save"})}
          idBase={"applicationsSave"}
          onClick={() => {
            setWaitingForResponse(true);
            API.createApp(newApp.trim()).then((response) => {
              setWaitingForResponse(false);
              if (response.success && response.payload) {
                setApps({
                  ...apps,
                  payload: [
                    ...(apps.payload || []),
                    {name: newApp.trim(), id: response.payload.id},
                  ],
                });
                setNewApp("");
                addSnackbar({
                  variant: "timeout",
                  text: intl.formatMessage(
                    {
                      id: "applications.snackbar.create.success",
                    },
                    {name: newApp.trim()},
                  ),
                  timeout: 7000,
                });
              } else {
                addSnackbar({
                  variant: "close",
                  text: intl.formatMessage({
                    id: "applications.snackbar.create.error",
                  }),
                  hasCloseButton: true,
                });
              }
            });
          }}
          configuration={"tertiary"}
          isDisabled={!validNewAppName || waitingForResponse}
        />
      </div>
      {apps.unresolved ||
      (apps.success && apps.payload && apps.payload.length > 0) ? (
        <Table
          id={"applicationsTable"}
          isLoading={apps.unresolved}
          data={(apps.payload || []).map((app) => ({
            id: app.id,
            cells: {
              name: {content: app.name, hasToolTip: true},
              domain: app.app_domain
                ? {
                    content: app.app_domain.name,
                    hasToolTip: true,
                    link: {
                      to: "/domains/overview/" + app.app_domain.id,
                      onClick: () => {
                        if (app.app_domain) {
                          localStorage.setItem(
                            "appDomainCreationOriginPage",
                            "other/applications",
                          );
                        }
                      },
                    },
                  }
                : {
                    sortingKey: "",
                    value: (
                      <span className="blank">
                        <FormattedMessage id="reusables.table.emptyEntry" />
                      </span>
                    ),
                  },
              delete: {
                sortingKey: "",
                value: (
                  <IconButton
                    idBase={"applicationsDelete" + app.id}
                    onClick={() => {
                      modalContext.showModal({
                        idBase: "applicationsDeletion",
                        headline: intl.formatMessage({
                          id: "applications.modal.headline",
                        }),
                        body: intl.formatMessage({
                          id: "applications.modal.text",
                        }),
                        primaryButton: {
                          name: intl.formatMessage({
                            id: "applications.modal.delete",
                          }),
                          onClick: () => deleteApp(app.id),
                        },
                        secondaryButton: {
                          name: intl.formatMessage({
                            id: "applications.modal.cancel",
                          }),
                          onClick: () => undefined,
                        },
                      });
                    }}
                    hoverColor="var(--semTableColorIconHover)"
                    buttonSize={{height: 48, width: 48}}
                    iconProps={{
                      href: trashIcon + "#globalG",
                      strokeColor: app.app_domain
                        ? "var(--colorBtnSecondaryIconDisabled)"
                        : "var(--colorBtnSecondaryIconHover)",
                      title: intl.formatMessage({
                        id: "applications.table.delete",
                      }),
                    }}
                    disabled={
                      !!app.app_domain || waitingForDeletion.includes(app.id)
                    }
                  />
                ),
              },
            },
            expandableContent:
              screenSize.category === "Mobile" ? (
                <div className="applicationsExpandContainer">
                  <div>
                    <div>
                      <span className="applicationsExpandLabel">
                        <FormattedMessage id="applications.expand.domain" />
                      </span>
                      {app.app_domain ? (
                        <a href={"/domains/overview/" + app.app_domain.id}>
                          {app.app_domain.name}
                        </a>
                      ) : (
                        <span className="blank">
                          <FormattedMessage id="reusables.table.emptyEntry" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : undefined,
          }))}
          columns={[
            {
              key: "name",
              label: <FormattedMessage id="applications.table.name" />,
              sortable: true,
              columnCount: {
                "Big Desktop": 6,
                Desktop: 6,
                Tablet: 4,
                Mobile: 3,
              },
            },
            {
              key: "domain",
              label: <FormattedMessage id="applications.table.domain" />,
              sortable: true,
              collapsibleMobile: true,
              columnCount: {
                "Big Desktop": 5,
                Desktop: 5,
                Tablet: 3,
              },
            },
            {
              key: "delete",
              label: <FormattedMessage id="applications.table.delete" />,
              srOnlyLabel: true,
              columnCount: {
                "Big Desktop": 1,
                Desktop: 1,
                Tablet: 1,
                Mobile: 1,
              },
            },
          ]}
          ariaLabelledBy="applicationsHeader"
          pageSize={12}
        />
      ) : (
        <p className="applicationsNoTable">
          <FormattedMessage
            id={
              "applications.table.error." +
              (apps.payload
                ? "blank"
                : apps.unresolved
                  ? "unresolved"
                  : "failed")
            }
          />
        </p>
      )}
    </div>
  );
}

export {Applications, type UploadFile};
